import {useGetActiveShiftCandidatesQuery, useGetAllCurrentLocationsQuery} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {useCallback, useEffect, useMemo, useState} from "react";
import {buildActiveShiftRows} from "./active-shifts";

/** One region-scoped shift query, plus one shared bulk location query while the tray is open. */
export function useActiveShifts(regionId: string | undefined, open: boolean) {
  const [now, setNow] = useState(Date.now);
  const shifts = useGetActiveShiftCandidatesQuery(regionId ? {regionId} : skipToken, {
    pollingInterval: 30_000,
    refetchOnMountOrArgChange: true,
  });
  const locations = useGetAllCurrentLocationsQuery(regionId && open ? {regionId, entityType: "PERSON"} : skipToken, {
    pollingInterval: 30_000,
    refetchOnMountOrArgChange: 30,
  });
  const reload = useCallback(() => {
    if (regionId) void shifts.refetch();
    if (regionId && open) void locations.refetch();
    setNow(Date.now());
  }, [regionId, open, shifts.refetch, locations.refetch]);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    window.addEventListener("focus", reload);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", reload);
    };
  }, [reload]);
  const snapshotNow = Math.max(now, shifts.fulfilledTimeStamp ?? 0, locations.fulfilledTimeStamp ?? 0);
  // currentData is essential: a region change must never show the previous region's count or rows.
  const rows = useMemo(
    () => buildActiveShiftRows(shifts.currentData ?? [], locations.currentData ?? [], regionId ?? "", snapshotNow),
    [shifts.currentData, locations.currentData, regionId, snapshotNow]
  );
  return {
    rows,
    now: snapshotNow,
    reload,
    loading: !!regionId && !shifts.currentData && (shifts.isLoading || shifts.isFetching),
    refreshing: shifts.isFetching || (open && locations.isFetching),
    error: shifts.isError ? "Active shifts could not be refreshed. Retrying automatically." : undefined,
    locationsLoading: open && !locations.currentData && locations.isFetching,
    locationsError:
      open && locations.isError ? "Locations are unavailable. The active shift count is unaffected." : undefined,
    updatedAt: shifts.fulfilledTimeStamp,
  };
}
